import { API_URL } from "../../shared/constants";
import { emptySplitApi } from "../emptySplit-api";

// Epic 2 (WhatsApp Integration) — Web admin contracts for stages, templates
// and the communication log. POST-only endpoints under `/whatsapp/*`.

export interface WhatsAppStage {
  stageId: string;
  stageKey: string;
  stageLabel: string;
  isDefaultOn: boolean;
  isEnabled: boolean;
  hasTemplate?: boolean;
  interaktTemplateName?: string | null;
  displayOrder: number;
}

export interface WhatsAppStageListResponse {
  status: boolean;
  count: number;
  list: WhatsAppStage[];
}

export interface WhatsAppTemplate {
  templateId: string;
  stageId?: string;
  stageKey: string;
  stageLabel: string;
  templateBody: string;
  placeholdersUsed: string[];
  interaktTemplateName: string;
  interaktTemplateId: string;
  parameterOrder: string[];
  variableMapping?: { interaktVar: string; dbField: string }[];
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface WhatsAppTemplateListResponse {
  status: boolean;
  count: number;
  list: WhatsAppTemplate[];
}

export interface WhatsAppLogItem {
  logId: string;
  customerName: string;
  contactNumber: string;
  orderNumber: string;
  stageKey: string;
  stageName?: string;
  stageLabel?: string;
  messageType: string;
  jobState: string;
  deliveryStatus: string;
  providerMessageId: string;
  retryCount: number;
  errorCode?: string;
  errorMessage?: string;
  statusTimestamp?: string;
  createdAt?: string;
}

export interface WhatsAppLogListResponse {
  status: boolean;
  count: number;
  list: WhatsAppLogItem[];
}

export interface WhatsAppLogListArgs {
  limit?: number;
  skip?: number;
  sort?: string;
  orderNumber?: string;
  deliveryStatus?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface WhatsAppStagesToggleArgs {
  id: string;
  data: { isEnabled: boolean };
}

export interface WhatsAppTemplatesCreateArgs {
  data: {
    stageId?: string;
    stageKey?: string;
    stageName?: string;
    stageLabel?: string;
    templateBody: string;
    interaktTemplateName: string;
    interaktTemplateId?: string;
    parameterOrder?: string[];
    variableMapping?: { interaktVar: string; dbField: string }[];
  };
}

export interface WhatsAppTemplatesUpdateArgs {
  id: string;
  data: {
    stageName?: string;
    stageLabel?: string;
    templateBody?: string;
    interaktTemplateName?: string;
    interaktTemplateId?: string;
    parameterOrder?: string[];
    variableMapping?: { interaktVar: string; dbField: string }[];
  };
}

export interface WhatsAppTemplatesPreviewArgs {
  templateId: string;
  orderId?: string;
}

export interface WhatsAppPreviewResponse {
  status: boolean;
  preview: string;
  params: Record<string, string>;
  characterCount: number;
  missingPlaceholders: string[];
  needsInteraktApproval: boolean;
}

export interface WhatsAppLogsExportArgs {
  format: "csv" | "xlsx";
  orderNumber?: string;
  deliveryStatus?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface WhatsAppMutationResponse {
  status: boolean;
  message: string;
}

export interface WhatsAppExportResponse {
  status: boolean;
  item?: string;
  file?: string;
  message?: string;
}

export const whatsappApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    whatsappStagesList: builder.query<WhatsAppStageListResponse, void>({
      query: () => ({
        url: `/${API_URL.WHATSAPP}/stages/list`,
        method: "post",
        body: {},
      }),
    }),
    whatsappStagesToggle: builder.mutation<
      WhatsAppMutationResponse,
      WhatsAppStagesToggleArgs
    >({
      query: ({ id, data }) => ({
        url: `/${API_URL.WHATSAPP}/stages/toggle/${id}`,
        method: "post",
        body: data,
      }),
    }),
    whatsappTemplatesList: builder.query<WhatsAppTemplateListResponse, void>({
      query: () => ({
        url: `/${API_URL.WHATSAPP}/templates/list`,
        method: "post",
        body: {},
      }),
    }),
    whatsappTemplatesCreate: builder.mutation<
      WhatsAppMutationResponse,
      WhatsAppTemplatesCreateArgs
    >({
      query: ({ data }) => ({
        url: `/${API_URL.WHATSAPP}/templates/create`,
        method: "post",
        body: data,
      }),
    }),
    whatsappTemplatesUpdate: builder.mutation<
      WhatsAppMutationResponse,
      WhatsAppTemplatesUpdateArgs
    >({
      query: ({ id, data }) => ({
        url: `/${API_URL.WHATSAPP}/templates/update/${id}`,
        method: "post",
        body: data,
      }),
    }),
    whatsappTemplatesPreview: builder.mutation<
      WhatsAppPreviewResponse,
      WhatsAppTemplatesPreviewArgs
    >({
      query: (data) => ({
        url: `/${API_URL.WHATSAPP}/templates/preview`,
        method: "post",
        body: data,
      }),
    }),
    whatsappLogsList: builder.query<WhatsAppLogListResponse, WhatsAppLogListArgs>({
      query: (data) => ({
        url: `/${API_URL.WHATSAPP}/logs/list`,
        method: "post",
        body: data,
      }),
    }),
    whatsappLogsExport: builder.mutation<
      WhatsAppExportResponse,
      WhatsAppLogsExportArgs
    >({
      query: (data) => ({
        url: `/${API_URL.WHATSAPP}/logs/export`,
        method: "post",
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useWhatsappStagesListQuery,
  useWhatsappStagesToggleMutation,
  useWhatsappTemplatesListQuery,
  useWhatsappTemplatesCreateMutation,
  useWhatsappTemplatesUpdateMutation,
  useWhatsappTemplatesPreviewMutation,
  useWhatsappLogsListQuery,
  useWhatsappLogsExportMutation,
} = whatsappApi;
